export type OnboardingStepKey =
  | 'first_message'
  | 'first_call'
  | 'add_contacts'
  | 'webinar'
  | 'broadcast'
  | 'textbot'
  | 'advanced_features'

export type OnboardingStepStatus = 'complete' | 'incomplete' | 'unknown'

export interface OnboardingStep {
  id: number
  key: OnboardingStepKey
  title: string
  status: OnboardingStepStatus
  signal: string
}

/**
 * Fetchable step signals: `true`/`false` when a front-end source can decide,
 * `null` when that source was unavailable (→ reported as `unknown`, never guessed).
 * The webinar and advanced-features steps have no reliable in-app signal and are
 * always `unknown`, so they are not part of this input.
 */
export interface OnboardingSignals {
  hasSentMessage: boolean | null
  hasMadeCall: boolean | null
  hasContacts: boolean | null
  hasBroadcast: boolean | null
  hasTextbot: boolean | null
}

export interface OnboardingStatus {
  steps: OnboardingStep[]
  /** Steps the user has confirmed NOT done — the actionable "N steps left". */
  remainingCount: number
  /** Steps with no reliable signal; the co-pilot asks the user about these. */
  unknownCount: number
  /** True when no step is confirmed-incomplete (unknown steps do not block it). */
  allComplete: boolean
}

type SignalKey = keyof OnboardingSignals

// Checklist order (mirrors the official 7-step onboarding checklist, SWC-18869 UC-8).
// `signalKey: null` → no in-app signal, always `unknown`.
const STEP_DEFS: ReadonlyArray<{
  id: number
  key: OnboardingStepKey
  title: string
  signalKey: SignalKey | null
}> = [
  { id: 1, key: 'first_message', title: 'Send your first message', signalKey: 'hasSentMessage' },
  { id: 2, key: 'first_call', title: 'Make your first call', signalKey: 'hasMadeCall' },
  { id: 3, key: 'add_contacts', title: 'Add your contacts', signalKey: 'hasContacts' },
  { id: 4, key: 'webinar', title: 'Master Salesmsg (webinar)', signalKey: null },
  { id: 5, key: 'broadcast', title: 'Schedule a broadcast', signalKey: 'hasBroadcast' },
  { id: 6, key: 'textbot', title: 'Enable your first AI Agent', signalKey: 'hasTextbot' },
  { id: 7, key: 'advanced_features', title: 'Explore advanced features', signalKey: null },
]

/** A step whose read threw, as opposed to one that has no in-app signal at all. */
const SIGNAL_UNAVAILABLE = 'signal unavailable'

export const deriveOnboardingSteps = (signals: OnboardingSignals): OnboardingStatus => {
  const steps = STEP_DEFS.map(({ id, key, title, signalKey }): OnboardingStep => {
    if (signalKey === null) {
      return { id, key, title, status: 'unknown', signal: 'no in-app signal' }
    }
    const value = signals[signalKey]
    if (value === null) return { id, key, title, status: 'unknown', signal: SIGNAL_UNAVAILABLE }
    return value
      ? { id, key, title, status: 'complete', signal: 'done' }
      : { id, key, title, status: 'incomplete', signal: 'not done yet' }
  })

  const remainingCount = steps.filter((s) => s.status === 'incomplete').length
  const unknownCount = steps.filter((s) => s.status === 'unknown').length
  // A failed read is not evidence that nothing is outstanding: counting it as
  // complete let a partial outage report a set-up account (research R12).
  const unreadable = steps.some((s) => s.signal === SIGNAL_UNAVAILABLE)
  return { steps, remainingCount, unknownCount, allComplete: remainingCount === 0 && !unreadable }
}
